import React, { createContext, useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import blockedCountriesData from '../data/blockedCountries.json';
import LangDropdownList from '../components/LangDropdown';
import '../styles/Footer.css';
import { getApiUrl } from "../utils/config";

const GeoContext = createContext();

// Получение IP-адреса пользователя через собственный API
const getUserIP = async () => {
    try {
        // Создаем AbortController для таймаута
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд таймаут

        const response = await fetch(`${getApiUrl()}/api/client-geo`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success) {
            return {
                ip: data.ip,
                country: data.country,
                countryName: data.countryName,
                detectionMethod: data.detectionMethod,
                city: 'Unknown', // Пока не определяем город
                region: 'Unknown'
            };
        } else {
            throw new Error(data.message || 'Сервер вернул ошибку');
        }
    } catch (error) {
        if (error.name === 'AbortError') {
            console.error('🌍 Геопроверка превысила таймаут (5 сек) - разрешаем доступ');
        } else if (error.message.includes('Failed to fetch')) {
            console.error('🌍 Сеть недоступна или CORS блокировка - разрешаем доступ');
        } else {
            console.error('🌍 Ошибка при получении геолокации с сервера:', error);
        }

        // Логируем детали для мобильной отладки
        if (typeof window !== 'undefined' && window.addLog) {
            window.addLog(`🌍 GeoContext error: ${error.name} - ${error.message}`);
        }

        // Если не удалось получить данные - не определяем геолокацию
        console.log('🌍 Не удалось получить геолокацию с сервера - разрешаем доступ');
        return null;
    }
};

// Проверка, заблокирована ли страна
const isCountryBlocked = (countryCode) => {
    if (!countryCode) return false;

    const blockedCountries = blockedCountriesData.blockedCountries;
    return blockedCountries.some(country =>
        country.code.toLowerCase() === countryCode.toLowerCase()
    );
};

// Получение названия заблокированной страны
const getBlockedCountryName = (countryCode) => {
    if (!countryCode) return null;

    const blockedCountries = blockedCountriesData.blockedCountries;
    const country = blockedCountries.find(country =>
        country.code.toLowerCase() === countryCode.toLowerCase()
    );

    return country ? country.name : null;
};

// Проверка блокировки без кеширования
const checkGeoBlocking = async () => {
    // Проверяем обход блокировки для администраторов
    const urlParams = new URLSearchParams(window.location.search);
    const bypassParam = urlParams.get('bypass_geo');
    const storedBypass = localStorage.getItem('geo_bypass');

    if (bypassParam === 'admin2025' || storedBypass === 'true') {
        if (bypassParam === 'admin2025') {
            localStorage.setItem('geo_bypass', 'true');
        }
        return {
            isBlocked: false,
            userIp: 'Bypass enabled',
            userCountry: null,
            userCountryName: 'Геоблокировка отключена'
        };
    }

    // Получаем данные о местоположении (каждый раз заново)
    console.log('🌍 Проверяем геолокацию...');
    const geoData = await getUserIP();

    if (!geoData) {
        // Если не удалось получить данные - не блокируем
        console.log('🌍 Не удалось определить геолокацию - доступ разрешен');
        return {
            isBlocked: false,
            userIp: 'Не определен',
            userCountry: null,
            userCountryName: 'Определение геолокации недоступно'
        };
    }

    // Блокируем только при точном определении через Cloudflare
    let shouldBlock = false;
    if (geoData.detectionMethod === 'cloudflare') {
        // Точное определение через Cloudflare - блокируем согласно списку
        shouldBlock = isCountryBlocked(geoData.country);
    }
    // Для всех остальных методов (unknown и т.д.) - не блокируем

    const result = {
        isBlocked: shouldBlock,
        userIp: geoData.ip,
        userCountry: geoData.country,
        userCountryName: geoData.countryName
    };

    // Логируем результат для отладки
    console.log('🌍 Результат геоблокировки:', {
        isBlocked: result.isBlocked,
        country: result.userCountry,
        countryName: result.userCountryName,
        ip: result.userIp,
        method: geoData.detectionMethod
    });

    return result;
};

// Глобальные функции для отладки (доступны в консоли браузера)
if (typeof window !== 'undefined') {
    window.resetGeoBlocking = () => {
        localStorage.removeItem('geo_bypass');
        console.log('🌍 Геоблокировка сброшена. Перезагрузите страницу.');
    };

    window.enableGeoBypass = () => {
        localStorage.setItem('geo_bypass', 'true');
        console.log('🌍 Обход геоблокировки включен. Перезагрузите страницу.');
    };

    window.disableGeoBypass = () => {
        localStorage.removeItem('geo_bypass');
        console.log('🌍 Обход геоблокировки отключен. Перезагрузите страницу.');
    };
}

export const GeoProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [geoBlocking, setGeoBlocking] = useState({
        isChecking: true,
        isBlocked: false,
        userIp: null,
        userCountry: null,
        userCountryName: null
    });

    // Состояние для глобального языкового dropdown
    const [globalLangDropdown, setGlobalLangDropdown] = useState({
        isOpen: false,
        position: { x: 0, y: 0 },
        triggerElement: null,
        isMobile: false
    });

    // Проверка геоблокировки при загрузке
    useEffect(() => {
        const checkBlocking = async () => {
            try {
                console.log('🌍 Начинаем проверку геоблокировки...');
                const result = await checkGeoBlocking();

                setGeoBlocking({
                    isChecking: false,
                    isBlocked: result.isBlocked,
                    userIp: result.userIp,
                    userCountry: result.userCountry,
                    userCountryName: result.userCountryName
                });

                console.log('🌍 Проверка геоблокировки завершена:', {
                    isBlocked: result.isBlocked,
                    country: result.userCountry,
                    ip: result.userIp
                });
            } catch (error) {
                console.error('❌ Ошибка при проверке геоблокировки:', error);
                // В случае ошибки не блокируем пользователя
                setGeoBlocking({
                    isChecking: false,
                    isBlocked: false,
                    userIp: null,
                    userCountry: null,
                    userCountryName: null
                });
            }
        };

        // Аварийный таймер - если геопроверка не завершилась за 10 секунд, разблокируем приложение
        const emergencyTimeout = setTimeout(() => {
            console.error('⚠️ Аварийное разблокирование: геопроверка превысила 10 секунд');
            setGeoBlocking({
                isChecking: false,
                isBlocked: false,
                userIp: 'Timeout',
                userCountry: null,
                userCountryName: 'Геопроверка превысила таймаут'
            });
        }, 10000);

        checkBlocking().finally(() => {
            clearTimeout(emergencyTimeout);
        });

        return () => {
            clearTimeout(emergencyTimeout);
        };
    }, []);

    // Функции для управления геоблокировкой (для администраторов)
    const resetGeoBlocking = () => {
        localStorage.removeItem('geo_bypass');
        window.location.reload();
    };

    const enableGeoBypass = () => {
        localStorage.setItem('geo_bypass', 'true');
        window.location.reload();
    };

    const disableGeoBypass = () => {
        localStorage.removeItem('geo_bypass');
        window.location.reload();
    };

    // Функции для управления глобальным языковым dropdown
    const openGlobalLangDropdown = (triggerElement) => {
        if (!triggerElement) return;

        const rect = triggerElement.getBoundingClientRect();
        const isMobile = window.innerWidth <= 768;
        
        const position = isMobile 
            ? { x: '50%', y: 'auto' } // На мобильных позиционируется через CSS
            : { x: rect.left + rect.width / 2, y: rect.top };

        setGlobalLangDropdown({
            isOpen: true,
            position,
            triggerElement,
            isMobile
        });
    };

    const closeGlobalLangDropdown = () => {
        setGlobalLangDropdown({
            isOpen: false,
            position: { x: 0, y: 0 },
            triggerElement: null,
            isMobile: false
        });
    };

    const handleGlobalLanguageChange = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem("language", lang);
        closeGlobalLangDropdown();
    };

    // Обработка изменения размера окна
    useEffect(() => {
        const handleResize = () => {
            if (globalLangDropdown.isOpen) {
                const newIsMobile = window.innerWidth <= 768;
                if (newIsMobile !== globalLangDropdown.isMobile) {
                    // Обновляем состояние, если изменился тип устройства
                    setGlobalLangDropdown(prev => ({
                        ...prev,
                        isMobile: newIsMobile,
                        position: newIsMobile 
                            ? { x: '50%', y: 'auto' }
                            : { x: prev.position.x, y: prev.position.y }
                    }));
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [globalLangDropdown.isOpen, globalLangDropdown.isMobile]);

    // Закрытие dropdown при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (globalLangDropdown.isOpen && globalLangDropdown.triggerElement) {
                const dropdownElement = document.querySelector('.global-lang-dropdown');
                if (dropdownElement && !dropdownElement.contains(event.target) &&
                    !globalLangDropdown.triggerElement.contains(event.target)) {
                    closeGlobalLangDropdown();
                }
            }
        };

        if (globalLangDropdown.isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [globalLangDropdown.isOpen, globalLangDropdown.triggerElement]);

    return (
        <GeoContext.Provider value={{
            geoBlocking,
            isGeoChecking: geoBlocking.isChecking,
            isBlocked: geoBlocking.isBlocked,
            userIp: geoBlocking.userIp,
            userCountry: geoBlocking.userCountry,
            userCountryName: geoBlocking.userCountryName,
            // Утилиты
            isCountryBlocked,
            getBlockedCountryName,
            // Административные функции
            resetGeoBlocking,
            enableGeoBypass,
            disableGeoBypass,
            // Глобальный языковой dropdown
            globalLangDropdown,
            openGlobalLangDropdown,
            closeGlobalLangDropdown,
            handleGlobalLanguageChange
        }}>
            {children}
            {/* Глобальный языковой dropdown */}
            {globalLangDropdown.isOpen && (
                <div
                    className="global-lang-dropdown"
                    style={globalLangDropdown.isMobile 
                        ? {
                            position: 'fixed',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 9999,
                            pointerEvents: 'none'
                        }
                        : {
                            position: 'fixed',
                            left: globalLangDropdown.position.x,
                            top: globalLangDropdown.position.y,
                            transform: 'translateX(-50%) translateY(-100%)',
                            zIndex: 9999,
                            marginTop: '-8px'
                        }
                    }
                >
                    <LangDropdownList
                        currentLang={i18n.language}
                        onSelect={handleGlobalLanguageChange}
                        onClose={closeGlobalLangDropdown}
                        dropdownClassName="footer-lang-dropdown-list"
                        style={globalLangDropdown.isMobile 
                            ? {
                                position: 'fixed',
                                left: '50%',
                                bottom: '24px',
                                transform: 'translateX(-50%)',
                                pointerEvents: 'auto'
                            }
                            : {
                                position: 'relative',
                                transform: 'none',
                                left: 'auto',
                                right: 'auto',
                                bottom: 'auto'
                            }
                        }
                    />
                </div>
            )}
        </GeoContext.Provider>
    );
};

export const useGeo = () => {
    const context = useContext(GeoContext);
    if (!context) {
        throw new Error('useGeo должен использоваться внутри GeoProvider');
    }
    return context;
}; 